import { getAnonymousExternalId } from './util/anonymousId'

// Per-user identity that the AnalyticsClient stamps onto every outgoing event.
// Kept as a tiny mutable singleton (rather than React/Redux state) because
// `analytics.track()` is called synchronously from non-component code paths
// (hook bodies, Promise callbacks) and must not depend on a render pass.
//
// Field semantics — matches the wire contract in CAPI.md §7.1:
//   external_id : opaque per-user id. Anonymous: persistent UUID from
//                 `anonymousId.ts`. Authenticated: backend user id (Cognito
//                 sub / DB id). Always populated.
//   email       : raw email; backend hashes (SHA-256) before egress to vendors.
//   phone       : raw E.164 phone; backend hashes before egress.
//
// The backend backfills `email`/`phone`/`external_id` from the user record when
// a JWT is present, so for authenticated traffic this layer is "advisory":
// supplying values is harmless but not required. For anonymous traffic
// (including guest checkout) it is the only source.

export interface AnalyticsIdentity {
  external_id?: string
  email?: string
  phone?: string
}

let current: AnalyticsIdentity = {}

function trim(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const t = value.trim()
  return t.length > 0 ? t : undefined
}

/**
 * Returns the identity to stamp on the next event. Always includes
 * `external_id` — falls back to the persisted anonymous UUID when no
 * authenticated value has been pushed in.
 */
export function getAnalyticsIdentity(): AnalyticsIdentity {
  const externalId = current.external_id ?? getAnonymousExternalId()
  return {
    external_id: externalId,
    email: current.email,
    phone: current.phone,
  }
}

/**
 * Merge-patch the in-memory identity. Pass `undefined` for a key to clear it
 * (e.g. user removed phone from profile). Empty strings are treated as cleared
 * to avoid sending "" to vendors.
 */
export function setAnalyticsIdentity(patch: AnalyticsIdentity): void {
  const next: AnalyticsIdentity = { ...current }
  if ('external_id' in patch) next.external_id = trim(patch.external_id)
  if ('email' in patch) next.email = trim(patch.email)
  if ('phone' in patch) next.phone = trim(patch.phone)
  current = next
}

/**
 * Reset to anonymous state. Called on signOut. Keeps the persisted anonymous
 * external_id (so the same device → same anon id across logout/login cycles).
 */
export function clearAnalyticsIdentity(): void {
  current = {}
}
